import { Component, ErrorInfo, ReactNode } from 'react';
import { reportErrorFromBoundary } from '../../monitoring/errorReporting';
import { isRecoverableReactDomNoise } from '../../utils/isRecoverableReactDomNoise';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  retryCount: number;
}

/**
 * Specialized error boundary for React DOM manipulation errors
 * that can occur during hot module replacement or React StrictMode
 */
export class ReactDOMErrorBoundary extends Component<Props, State> {
  private readonly maxRetries = 3;
  private retryTimeout?: ReturnType<typeof setTimeout>;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: isRecoverableReactDomNoise(error),
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('React DOM Error Boundary caught an error:', error, errorInfo);

    // Recoverable React DOM reconciliation noise (HMR/StrictMode) — do not report to Sentry.
    if (!isRecoverableReactDomNoise(error)) {
      reportErrorFromBoundary(error, {
        boundary: 'react-dom',
        componentStack: errorInfo.componentStack,
      });
    }

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // For React DOM errors, try to recover automatically
    if (isRecoverableReactDomNoise(error)) {
      // Mark that we've had DOM errors to disable StrictMode
      localStorage.setItem('react-dom-errors', 'true');
      this.attemptRecovery();
    }
  }

  private readonly attemptRecovery = () => {
    const { retryCount } = this.state;

    if (retryCount >= this.maxRetries) {
      console.error('Max retry attempts reached, giving up recovery');
      return;
    }

    console.log(`Attempting recovery (${retryCount + 1}/${this.maxRetries})...`);

    // Clear any existing timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    // Wait a bit before attempting recovery
    this.retryTimeout = setTimeout(
      () => {
        this.setState((prevState) => ({
          hasError: false,
          error: undefined,
          errorInfo: undefined,
          retryCount: prevState.retryCount + 1,
        }));
      },
      1000 * (retryCount + 1),
    ); // Exponential backoff
  };

  private readonly handleRetry = () => {
    // Clear the DOM error flag to re-enable StrictMode
    localStorage.removeItem('react-dom-errors');

    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: 0,
    });
  };

  private readonly handleReload = () => {
    window.location.reload();
  };

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  render() {
    if (this.state.hasError) {
      const { retryCount } = this.state;

      // If we have a custom fallback, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback for React DOM errors
      return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center px-4'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center'>
            <div className='flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full'>
              <svg
                className='w-8 h-8 text-yellow-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>

            <h1 className='text-xl font-semibold text-gray-900 mb-2'>React DOM Error</h1>

            <p className='text-gray-600 mb-6'>
              The application encountered a DOM manipulation error. This is usually caused by hot
              module replacement or React StrictMode. The app is attempting to recover
              automatically.
            </p>

            {retryCount > 0 && (
              <p className='text-sm text-gray-500 mb-4'>
                Recovery attempt {retryCount} of {this.maxRetries}
              </p>
            )}

            <div className='flex flex-col gap-3 mb-4'>
              <button
                onClick={this.handleRetry}
                className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors'
              >
                Try Again
              </button>

              <button
                onClick={this.handleReload}
                className='px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors'
              >
                Reload Page
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className='mt-4 text-left'>
                <summary className='cursor-pointer font-medium text-sm text-gray-700'>
                  Error details (development only)
                </summary>
                <pre className='mt-2 text-xs bg-gray-100 p-3 rounded overflow-auto max-h-32'>
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ReactDOMErrorBoundary;
